import { Link } from 'react-router-dom';
import { Panel } from '../ui/Panel';
import { useCan } from '../useCan';
import { useAdmin } from '../AdminContext';
import { useCosts } from '../margin/useCosts';
import { MarginLines } from '../margin/MarginLines';
import { basketMarginPercent, boxContribution, marginLines, orderContribution } from '../../core/rules/margin';
import { vatRateOf } from '../../core/rules/revenue';
import { euro, money, toCents } from '../../core/format/currency';
import type { Product } from '../../core/types/catalog';

/**
 * Protocol › Marge: wat er overblijft als iemand het hele protocol bestelt (gratis verzonden, iDEAL),
 * en wat een bijvuldoos met deze producten gemiddeld oplevert. Het staffelcadeau komt uit de
 * voorraad en telt in het overzicht per bestelling, niet hier. Alleen met `finance.margin`.
 */
export function RoutineMarginPanel({
  items
}: {
  items: Product[];
}) {
  const {
    canAll
  } = useCan();
  const {
    site
  } = useAdmin();
  const allowed = canAll('finance.margin');
  const {
    data
  } = useCosts(allowed);
  if (!allowed || items.length === 0) return null;
  return <Panel title="Marge" description="Zonder btw, met je ingeschatte kosten." data-id="element-915" data-mp="1xnej7hva2ytg">
      {!data ? <p className="text-[13px] text-muted" data-id="element-916" data-mp="2bmnp9oprscjp">Laden…</p> : <RoutineMarginBody items={items} itemCosts={data.itemCosts} costs={data.costs} minBox={site.policy.subscription.minBoxValue} data-id="element-917" data-mp="2bktdazwh7urr" />}
    </Panel>;
}
type BodyProps = {
  items: Product[];
  itemCosts: Record<string, number>;
  costs: NonNullable<ReturnType<typeof useCosts>['data']>['costs'];
  minBox: number;
};
function RoutineMarginBody({
  items,
  itemCosts,
  costs,
  minBox
}: BodyProps) {
  const lines = marginLines(items, itemCosts, vatRateOf);
  const gross = lines.reduce((s, l) => s + l.grossCents, 0);
  const percent = basketMarginPercent(lines);
  const first = orderContribution({
    kind: 'protocol',
    lines,
    paymentMethod: 'ideal',
    paidCents: gross,
    shipping: 'pakket-nl'
  }, costs);
  return <>
      <p className="text-[13px] text-muted" data-id="element-918" data-mp="v6tddoxms54i">Als iemand het hele protocol bestelt</p>
      <MarginLines breakdown={first} className="mt-2" data-id="element-919" data-mp="2v8syjsb9c6jc" />
      {percent !== null && <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed text-muted" data-id="element-920" data-mp="3085mkgdv7jni">
          Brutomarge {Math.round(percent)}%. Op bijvullen gaat alleen wat bijna op is; een doos op het minimum van {euro(minBox)} laat dan ±{' '}
          <span className="text-ink" data-id="element-921" data-mp="2ccb1cvdn3efp">{money(boxContribution(toCents(minBox), percent, costs))}</span> over.
        </p>}
      {first.missingCost.length > 0 && <Link to="/be/voorraad?tab=inkoop" className="mt-2 inline-block text-[13px] text-ink underline underline-offset-4" data-id="element-922" data-mp="2hd8gt079ludi">
          Inkoop boeken
        </Link>}
    </>;
}